/**
 * Course enrollments: who registered for which course, with payment details and pagination.
 *   mode="instructor"  the signed-in instructor's courses (dashboard → My Learners)
 *   mode="admin"       all courses, with instructor, platform fee and Stripe ids (admin → Course Enrollments)
 * Data: GET /api/enrollments/{instructor|admin} (login cookie), see routers/enrollments.py.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, InputAdornment, Link, MenuItem, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow,
  TextField, Tooltip, Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

type Mode = "instructor" | "admin";

interface EnrollmentRow {
  id: string;
  enrolled_at: string | null;
  student_name: string;
  student_email: string;
  course_id: string;
  course_title: string;
  course_url: string;
  type: "paid" | "free";
  amount: number;
  currency: string;
  discount: number;
  instructor_earning: number;
  payment_status: string;
  status: string;
  instructor_name?: string;
  instructor_email?: string;
  platform_fee?: number;
  payment_id?: string;
  payment_intent_id?: string;
}

interface Summary {
  total_enrollments: number;
  unique_learners: number;
  paid_enrollments: number;
  free_enrollments: number;
  gross_revenue: number;
  instructor_earnings: number;
  platform_fees?: number;
}

const API = process.env.REACT_APP_SERVER_ENDPOINT || "";

const money = (v: number, currency = "USD") => {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: currency || "USD" }).format(v || 0);
  } catch {
    return `${(v || 0).toFixed(2)} ${currency}`;
  }
};

const when = (iso: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) +
        ", " + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
};

const PALETTE = {
  dark: {
    text: "#fff", muted: "rgba(255,255,255,0.6)", card: "#151821", border: "rgba(255,255,255,0.08)",
    head: "#1b1f2a", rowHover: "rgba(255,255,255,0.03)", input: "#11141b", accent: "#4BA5EB",
  },
  light: {
    text: "#1f2937", muted: "#6b7280", card: "#ffffff", border: "#e5e7eb",
    head: "#f3f6fb", rowHover: "#fafbff", input: "#ffffff", accent: "#155bab",
  },
};

export default function CourseEnrollments({ mode, dark = mode === "instructor" }: { mode: Mode; dark?: boolean }) {
  const c = dark ? PALETTE.dark : PALETTE.light;
  const isAdmin = mode === "admin";

  const [rows, setRows] = useState<EnrollmentRow[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);              // TablePagination is 0-based, API is 1-based
  const [limit, setLimit] = useState(10);
  const [courses, setCourses] = useState<{ id: string; title: string }[]>([]);
  const [courseId, setCourseId] = useState("");
  const [type, setType] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const requestId = useRef(0);

  // debounce the search box
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(0); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    fetch(`${API}/api/enrollments/${mode}/courses`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { courses: [] }))
      .then((d) => setCourses(d.courses || []))
      .catch(() => setCourses([]));
  }, [mode]);

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setError("");
    const qs = new URLSearchParams({
      page: String(page + 1), limit: String(limit), course_id: courseId, type,
      search, from_date: fromDate, to_date: toDate,
    });
    try {
      const res = await fetch(`${API}/api/enrollments/${mode}?${qs.toString()}`, { credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (id !== requestId.current) return;          // a newer request is running
      if (!res.ok) {
        setRows([]); setTotal(0); setSummary(null);
        setError(res.status === 401 ? "Your session has expired. Please sign in again."
          : res.status === 403 ? "Only admins can see this page."
          : data?.detail || "Could not load enrollments.");
        return;
      }
      setRows(data.enrollments || []);
      setSummary(data.summary || null);
      setTotal(data.pagination?.total || 0);
    } catch {
      if (id === requestId.current) setError("Could not load enrollments. Please try again.");
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [mode, page, limit, courseId, type, search, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  const filtersActive = !!(courseId || type || search || fromDate || toDate);
  const clearFilters = () => {
    setCourseId(""); setType(""); setSearchInput(""); setSearch(""); setFromDate(""); setToDate(""); setPage(0);
  };

  const cards = useMemo(() => {
    if (!summary) return [];
    const list = [
      { label: "Enrollments", value: String(summary.total_enrollments) },
      { label: "Learners", value: String(summary.unique_learners) },
      { label: "Paid / Free", value: `${summary.paid_enrollments} / ${summary.free_enrollments}` },
      { label: "Gross revenue", value: money(summary.gross_revenue) },
      { label: isAdmin ? "Instructor payouts" : "Your earnings", value: money(summary.instructor_earnings) },
    ];
    if (isAdmin) list.push({ label: "Platform fees", value: money(summary.platform_fees || 0) });
    return list;
  }, [summary, isAdmin]);

  const inputSx = {
    "& .MuiOutlinedInput-root": { background: c.input, color: c.text, borderRadius: "8px", fontSize: 13.5 },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: c.border },
    "& .MuiInputLabel-root": { color: c.muted, fontSize: 13.5 },
    "& .MuiSvgIcon-root": { color: c.muted },
    "& input::-webkit-calendar-picker-indicator": { filter: dark ? "invert(1)" : "none" },
  };
  const cellSx = { color: c.text, borderColor: c.border, fontSize: 13, py: 1.25, verticalAlign: "top" };
  const headSx = { ...cellSx, color: c.muted, fontWeight: 600, background: c.head, whiteSpace: "nowrap" as const };

  const columns = isAdmin
    ? ["Enrolled", "Learner", "Course", "Instructor", "Type", "Paid", "Instructor share", "Platform fee", "Payment"]
    : ["Enrolled", "Learner", "Course", "Type", "Paid", "Your earning", "Payment"];

  return (
    <Box sx={{ color: c.text, width: "100%" }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} gap={1} mb={2}>
        <Box>
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: c.text }}>
            {isAdmin ? "Course Enrollments" : "My Learners"}
          </Typography>
          <Typography sx={{ fontSize: 13, color: c.muted }}>
            {isAdmin ? "Who registered for which course, across all instructors, with payment details."
              : "Who registered for your courses, and what they paid."}
          </Typography>
        </Box>
        <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading}
          sx={{ textTransform: "none", color: c.accent, alignSelf: { xs: "flex-start", sm: "center" } }}>
          Refresh
        </Button>
      </Stack>

      {cards.length > 0 && (
        <Box sx={{ display: "grid", gap: 1.5, mb: 2,
          gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))", md: `repeat(${cards.length}, minmax(0, 1fr))` } }}>
          {cards.map((card) => (
            <Box key={card.label} sx={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: "10px", p: 1.5 }}>
              <Typography sx={{ fontSize: 12, color: c.muted }}>{card.label}</Typography>
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: c.text, mt: 0.25 }}>{card.value}</Typography>
            </Box>
          ))}
        </Box>
      )}

      <Stack direction={{ xs: "column", md: "row" }} gap={1.25} mb={1.5} flexWrap="wrap">
        <TextField size="small" placeholder={isAdmin ? "Search learner, course or instructor" : "Search learner or course"}
          value={searchInput} onChange={(e) => setSearchInput(e.target.value)} sx={{ ...inputSx, minWidth: 240, flex: 1 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        <TextField select size="small" label="Course" value={courseId}
          onChange={(e) => { setCourseId(e.target.value); setPage(0); }} sx={{ ...inputSx, minWidth: 200 }}>
          <MenuItem value="">All courses</MenuItem>
          {courses.map((co) => <MenuItem key={co.id} value={co.id}>{co.title}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Type" value={type}
          onChange={(e) => { setType(e.target.value); setPage(0); }} sx={{ ...inputSx, minWidth: 120 }}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="paid">Paid</MenuItem>
          <MenuItem value="free">Free</MenuItem>
        </TextField>
        <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={fromDate}
          onChange={(e) => { setFromDate(e.target.value); setPage(0); }} sx={{ ...inputSx, minWidth: 150 }} />
        <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={toDate}
          onChange={(e) => { setToDate(e.target.value); setPage(0); }} sx={{ ...inputSx, minWidth: 150 }} />
        {filtersActive && (
          <Button size="small" onClick={clearFilters} sx={{ textTransform: "none", color: c.accent }}>Clear filters</Button>
        )}
      </Stack>

      <Box sx={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: "10px", overflow: "hidden" }}>
        <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: isAdmin ? 1100 : 820 }}>
            <TableHead>
              <TableRow>{columns.map((h) => <TableCell key={h} sx={headSx}>{h}</TableCell>)}</TableRow>
            </TableHead>
            <TableBody>
              {loading && rows.length === 0 ? (
                <TableRow><TableCell colSpan={columns.length} sx={{ ...cellSx, textAlign: "center", py: 5 }}>
                  <CircularProgress size={24} sx={{ color: c.accent }} />
                </TableCell></TableRow>
              ) : error ? (
                <TableRow><TableCell colSpan={columns.length} sx={{ ...cellSx, textAlign: "center", py: 5, color: "#ef4444" }}>
                  {error}
                </TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={columns.length} sx={{ ...cellSx, textAlign: "center", py: 5, color: c.muted }}>
                  {filtersActive ? "No enrollments match these filters." : "No one has enrolled yet."}
                </TableCell></TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.id} sx={{ "&:hover": { background: c.rowHover }, opacity: loading ? 0.6 : 1 }}>
                  <TableCell sx={{ ...cellSx, whiteSpace: "nowrap" }}>{when(r.enrolled_at)}</TableCell>
                  <TableCell sx={cellSx}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: c.text }}>{r.student_name || "—"}</Typography>
                    <Link href={`mailto:${r.student_email}`} sx={{ fontSize: 12, color: c.muted }} underline="hover">{r.student_email}</Link>
                  </TableCell>
                  <TableCell sx={{ ...cellSx, maxWidth: 260 }}>
                    {r.course_url ? (
                      <Link href={r.course_url} target="_blank" rel="noopener noreferrer" underline="hover"
                        sx={{ color: c.text, fontSize: 13, display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                        {r.course_title} <OpenInNewIcon sx={{ fontSize: 13, color: c.muted }} />
                      </Link>
                    ) : r.course_title}
                  </TableCell>
                  {isAdmin && (
                    <TableCell sx={cellSx}>
                      <Typography sx={{ fontSize: 13, color: c.text }}>{r.instructor_name || "—"}</Typography>
                      <Typography sx={{ fontSize: 12, color: c.muted }}>{r.instructor_email}</Typography>
                    </TableCell>
                  )}
                  <TableCell sx={cellSx}>
                    <Chip size="small" label={r.type === "paid" ? "Paid" : "Free"}
                      sx={{ height: 22, fontSize: 11.5, fontWeight: 600,
                        color: r.type === "paid" ? "#16a34a" : c.muted,
                        background: r.type === "paid" ? "rgba(22,163,74,0.12)" : (dark ? "rgba(255,255,255,0.06)" : "#f3f4f6") }} />
                  </TableCell>
                  <TableCell sx={{ ...cellSx, whiteSpace: "nowrap" }}>
                    {r.type === "paid" ? money(r.amount, r.currency) : "—"}
                    {r.discount > 0 && (
                      <Typography sx={{ fontSize: 11.5, color: c.muted }}>discount {money(r.discount, r.currency)}</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ ...cellSx, whiteSpace: "nowrap" }}>{r.type === "paid" ? money(r.instructor_earning, r.currency) : "—"}</TableCell>
                  {isAdmin && (
                    <TableCell sx={{ ...cellSx, whiteSpace: "nowrap" }}>{r.type === "paid" ? money(r.platform_fee || 0, r.currency) : "—"}</TableCell>
                  )}
                  <TableCell sx={cellSx}>
                    <Typography sx={{ fontSize: 12.5, color: c.text, textTransform: "capitalize" }}>{r.payment_status}</Typography>
                    {isAdmin && (r.payment_intent_id || r.payment_id) && (
                      <Tooltip title={`Checkout session: ${r.payment_id || "—"}`}>
                        <Typography sx={{ fontSize: 11.5, color: c.muted, fontFamily: "monospace", wordBreak: "break-all" }}>
                          {r.payment_intent_id || r.payment_id}
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={total === 0 ? 0 : Math.min(page, Math.max(0, Math.ceil(total / limit) - 1))}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={limit}
          onRowsPerPageChange={(e) => { setLimit(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ color: c.text, borderTop: `1px solid ${c.border}`,
            "& .MuiSvgIcon-root": { color: c.muted }, "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { fontSize: 13 } }}
        />
      </Box>
    </Box>
  );
}
